import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  Box,
  FormControl,
  InputLabel,
  TextField,
  TextFieldProps,
} from "@mui/material";
import Popover from "@mui/material/Popover";
import { useTheme } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PickerSelectionState } from "@mui/x-date-pickers/internals";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  DateRangeCalendar,
  DateRangeCalendarProps,
} from "@mui/x-date-pickers-pro";
import dayjs, { Dayjs } from "dayjs";
import React, { MouseEventHandler, useEffect, useState } from "react";
import ShortcutSection from "./components/ShortcutSection/ShortcutSection";
import { ConfigContext } from "../../../context/ConfigContext";

export interface DateRangePickerProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  onChange: (selection: [Dayjs, Dayjs]) => void;
  value?: [Dayjs | null, Dayjs | null];
  format?: string;
  renderValue?: (selection: (Dayjs | null)[]) => string;
  enableShortcuts?: boolean;
  textFieldProps?: TextFieldProps;
  dateRangeCalendarProps?: DateRangeCalendarProps;
  resetConfig?: {
    days: number;
    months: number;
  };
  shortcutLimit?: number;
  isDashboardDatePicker?: boolean;
}

const renderDates =
  (format: string) =>
  (selection: (Dayjs | null)[]): string => {
    if (selection.filter((val) => val).length === 0) return "";
    return selection.map((date) => date?.format(format)).join(" – ");
  };

const DateRangePicker = ({
  label = "Select Date",
  placeholder = "Start Date – End Date",
  required,
  error,
  onChange,
  value: initialValue = [null, null],
  helperText = "",
  format = "DD-MMM-YYYY",
  renderValue = renderDates(format),
  enableShortcuts = false,
  textFieldProps = {},
  dateRangeCalendarProps = {},
  resetConfig,
  shortcutLimit,
  isDashboardDatePicker,
}: DateRangePickerProps) => {
  const theme = useTheme();
  const { ENABLE_DASHBOARD_WIDGETS_V2 } = React.useContext(ConfigContext);

  const effectiveResetConfig = resetConfig ?? {
    days: ENABLE_DASHBOARD_WIDGETS_V2 ? dayjs().date() - 1 : 0,
    months: ENABLE_DASHBOARD_WIDGETS_V2 ? 0 : 3,
  };

  const [value, setValue] = useState(initialValue);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    // @ts-ignore
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleChange = (
    newValue: [Dayjs | null, Dayjs | null],
    selectionState?: PickerSelectionState | undefined
  ) => {
    setValue(newValue);
    if (selectionState === "finish" && newValue[0] && newValue[1]) {
      onChange([newValue[0], newValue[1]]);
      handleClose();
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? "date-range-popover" : undefined;

  return (
    <FormControl
      data-testid="date-range-picker"
      sx={{
        width: "fit-content",
      }}
    >
      {label && (
        <InputLabel data-testid="date-range-picker-label" required={required}>
          {label}
        </InputLabel>
      )}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TextField
          aria-describedby={id}
          onClick={handleClick}
          placeholder={placeholder}
          data-testid="date-range-picker-text-field"
          error={error}
          InputProps={{
            sx: {
              ...(error && {
                borderColor: theme.tokens.FuelColorAlertErrorBorder,
              }),

              ".MuiInputBase-input": {
                textOverflow: "initial",
                width: "13rem",
                fontSize: theme.tokens.FuelFontSizeSm,
              },
            },
            startAdornment: (
              <CalendarMonthIcon
                sx={{
                  marginLeft: "4px",
                  color: theme.tokens.FuelColorGray700,
                }}
              />
            ),
          }}
          helperText={helperText}
          value={renderValue(value)}
          sx={{
            width: isDashboardDatePicker ? "250px" : "fit-content",
          }}
          {...textFieldProps}
        />
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box sx={{ display: "flex" }}>
            {enableShortcuts && (
              <ShortcutSection
                currentSelection={value}
                onClick={(selection) => handleChange(selection, "finish")}
                resetConfig={effectiveResetConfig}
                shortcutLimit={shortcutLimit}
              />
            )}
            <DateRangeCalendar
              value={value}
              onChange={handleChange}
              {...dateRangeCalendarProps}
            />
          </Box>
        </Popover>
      </LocalizationProvider>
    </FormControl>
  );
};
export default DateRangePicker;
